import { UseMutationResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  userTrialPermissionsKeys,
  trialAccessRequestsKeys,
  publicTrialsKeys,
} from '@data/constants';
import { useTokenUserData, useTrial } from '@data/index';
import {
  AccessRequestStatus,
  AdminTrialAccessListView,
  Role,
  TrialAccessApproveBody,
  TrialAccessBody,
  TrialPermissionEnum,
  TrialPermissionsView,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { APIError, PermissionsQueryParams } from '@data/utils/types';

// TODO: move to queries
export const trialPermissionsQuery = ({
  trialId,
  enabled,
}: {
  enabled?: boolean;
  trialId: string;
}) => ({
  queryKey: userTrialPermissionsKeys.list(trialId),
  queryFn: async () => {
    const response = await MatchingApi.permissions.getUserTrialPermissions({ trialId });
    return response.data;
  },
  enabled: !!trialId && !!enabled,
});

export const useUserTrialPermissions = (trialId: string, enabled = true) => {
  return useQuery<TrialPermissionsView, Error>({
    queryKey: userTrialPermissionsKeys.list(trialId),
    queryFn: async () => {
      const response = await MatchingApi.permissions.getUserTrialPermissions({ trialId });
      return response.data;
    },
    enabled: !!trialId && enabled,
  });
};

// export const useAddUserTrialPermissions = (): UseMutationResult<
//   unknown,
//   any,
//   { trialId: string; values: UserTrialPermissionsBody },
//   unknown
// > => {
//   const queryClient = useQueryClient();
//   const { trialId: id = '' } = useParams<{ trialId: string }>();

//   return useMutation(
//     async ({ trialId, values }) => {
//       const response = await MatchingApi.permissions.addUserTrialPermissions({
//         trialId,
//         userTrialPermissionsBody: values,
//       });
//       return response.data;
//     },
//     {
//       onMutate: ({ values }) => {
//         const previousUserPermissions = queryClient.getQueryData<
//           InfiniteData<TrialPermissionsListView>
//         >(trialPermissionsKeys.list(id));

//         if (previousUserPermissions) {
//           const newPages = previousUserPermissions.pages.map(
//             (page: { items: TrialPermissionsView[] }) => ({
//               ...page,
//               items: page.items.map((user) =>
//                 user.userId === values.userId ? { ...user, permissions: values.permissions } : user,
//               ),
//             }),
//           );

//           queryClient.setQueryData(trialPermissionsKeys.list(id), {
//             ...previousUserPermissions,
//             pages: newPages,
//           });
//         }
//         return { previousUserPermissions };
//       },
//       onError: (_, __, context) => {
//         queryClient.setQueryData(trialPermissionsKeys.list(id), context?.previousUserPermissions);
//       },

//       onSuccess: () => {
//         queryClient.invalidateQueries(matchedVolunteersKeys.all());
//         // queryClient.invalidateQueries(userTrialPermissionsKeys.all());
//         queryClient.invalidateQueries(trialPermissionsKeys.all());
//       },
//     },
//   );
// };

// export const useRevokeAllUserTrialPermissions = (): UseMutationResult<
//   unknown,
//   any,
//   { trialId: string; userId: string },
//   unknown
// > => {
//   const queryClient = useQueryClient();
//   return useMutation(
//     async ({ trialId, userId }) => {
//       const response = await MatchingApi.permissions.deleteUserTrialPermissions({
//         trialId,
//         userId,
//       });
//       return response.data;
//     },
//     {
//       onSuccess: () => {
//         queryClient.invalidateQueries(userTrialPermissionsKeys.all());
//         queryClient.invalidateQueries(trialPermissionsKeys.all());
//       },
//     },
//   );
// };

export const useRequestTrialAccess = (): UseMutationResult<
  unknown,
  any,
  { trialId: string; values: TrialAccessBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.permissions.requestAccess({
        trialId,
        trialAccessBody: values,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: publicTrialsKeys.all() });
    },
  });
};

export const useAccessRequests = (status: AccessRequestStatus, params: PermissionsQueryParams) =>
  useQuery<AdminTrialAccessListView, Error>({
    queryKey: trialAccessRequestsKeys.lists(),
    queryFn: async () => {
      const response = await MatchingApi.permissions.getAccessRequests({ status, ...params });
      return response.data;
    },
    enabled: !!status,
  });

export const usePermissions = () => {
  const userData = useTokenUserData();

  const isTrialManager = userData.roles.includes(Role.TrialManager);
  const isAdmin = userData.roles.includes(Role.Admin);
  const isReviewer = userData.roles.includes(Role.Reviewer);
  const isPatientOrg = userData.roles.includes(Role.PatientOrg);
  const isPatient = userData.roles.includes(Role.Volunteer);
  const isSponsor = userData.roles.includes(Role.Sponsor);

  const { trialId = '' } = useParams<{ trialId: string }>();
  const { data: trial } = useTrial(trialId, undefined, !isPatient);
  const { data } = useUserTrialPermissions(
    trial?.trialId ?? '',
    userData && (isTrialManager || isPatientOrg || isSponsor),
  );

  const READ_TRIAL =
    isAdmin ||
    [
      TrialPermissionEnum.TrialRead,
      TrialPermissionEnum.TrialComment,
      TrialPermissionEnum.TrialWrite,
    ].some((v) => data?.permissions?.includes(v));

  const COMMENTATOR =
    data?.permissions?.includes(TrialPermissionEnum.TrialComment) &&
    !data?.permissions?.includes(TrialPermissionEnum.TrialWrite);

  const COMMENT_TRIAL =
    isAdmin ||
    [TrialPermissionEnum.TrialComment, TrialPermissionEnum.TrialWrite].some((v) =>
      data?.permissions?.includes(v),
    );

  const MANAGE_TRIAL = isAdmin || data?.permissions?.includes(TrialPermissionEnum.TrialWrite);

  const SEE_FUNNEL =
    isAdmin ||
    isReviewer ||
    isSponsor ||
    data?.permissions?.includes(TrialPermissionEnum.TrialFunnel) ||
    data?.permissions?.includes(TrialPermissionEnum.FunnelOverview);

  const IS_FUNNEL_VIEWER =
    data?.permissions?.includes(TrialPermissionEnum.FunnelOverview) &&
    !data?.permissions?.includes(TrialPermissionEnum.TrialFunnel);

  const READ_CONTACT_LOCATION = trial?.mandateLocationId ?? '';

  const READ_CONTACTS =
    data?.permissions?.includes(TrialPermissionEnum.TrialFunnel) &&
    data?.permissions?.includes(TrialPermissionEnum.ContactsRead);

  return {
    COMMENTATOR,
    READ_TRIAL,
    COMMENT_TRIAL,
    MANAGE_TRIAL,
    SEE_FUNNEL,
    READ_CONTACTS,
    IS_ADMIN: isAdmin,
    IS_REVIEWER: isReviewer,
    IS_TRIAL_MANAGER: isTrialManager,
    READ_CONTACT_LOCATION,
    IS_PATIENT_ORG: isPatientOrg,
    IS_FUNNEL_VIEWER,
    IS_SPONSOR: isSponsor,
    permissionsLoaded: !!data || isAdmin || isReviewer,
  };
};

export const useApproveTrialRequestAccess = (): UseMutationResult<
  unknown,
  any,
  { teamId: string; trialId: string; values: TrialAccessApproveBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, teamId, values }) => {
      const response = await MatchingApi.permissions.approveTrialAccessRequest({
        trialId,
        teamId,
        trialAccessApproveBody: values,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialAccessRequestsKeys.all() });
    },
  });
};

export const useRejectTrialRequestAccess = (): UseMutationResult<
  unknown,
  APIError,
  { responseMessage: string; teamId: string; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, teamId, responseMessage }) => {
      const response = await MatchingApi.permissions.rejectTrialAccessRequest({
        trialId,
        teamId,
        responseMessage,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialAccessRequestsKeys.all() });
    },
  });
};

// export const useLocationPermissions = (): UseMutationResult<
//   TrialLocationPermissionsListView,
//   APIError,
//   { locationId: string; trialId: string },
//   unknown
// > => {
//   return useMutation(async ({ locationId, trialId }) => {
//     const response = await MatchingApi.permissions.getLocationPermissions({ locationId, trialId });
//     return response.data;
//   });
// };

export const useUpdatePermissionTimestamp = (): UseMutationResult<
  void,
  APIError,
  { trialId: string },
  unknown
> =>
  useMutation({
    mutationFn: async ({ trialId }) => {
      const response = await MatchingApi.permissions.updatePermissionTimestamp({
        trialId,
        body: {},
      });
      return response.data;
    },
  });
